import React, { useMemo, useCallback, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiFilter, FiTarget, FiX, FiInfo, FiChevronDown } = FiIcons

const QuickNavigation = () => {
  const { 
    currentLang, 
    activeRange, 
    setQuickNavRange, 
    totalCount,
    searchQuery,
    originalTotalCount,
    translations
  } = useApp()

  // 新增折疊狀態
  const [isCollapsed, setIsCollapsed] = useState(false)

  const ranges = useMemo(() => [
    { id: 'all', label: { en: 'All', zh: '全部' } },
    { id: '001-030', label: { en: '001-030', zh: '001-030' }, start: 1, end: 30 },
    { id: '031-060', label: { en: '031-060', zh: '031-060' }, start: 31, end: 60 },
    { id: '061-090', label: { en: '061-090', zh: '061-090' }, start: 61, end: 90 },
    { id: '091-120', label: { en: '091-120', zh: '091-120' }, start: 91, end: 120 },
    { id: '121-150', label: { en: '121-150', zh: '121-150' }, start: 121, end: 150 },
    { id: '151-180', label: { en: '151-180', zh: '151-180' }, start: 151, end: 180 },
    { id: '181-210', label: { en: '181-210', zh: '181-210' }, start: 181, end: 210 },
    { id: '211-240', label: { en: '211-240', zh: '211-240' }, start: 211, end: 240 },
    { id: '241-270', label: { en: '241-270', zh: '241-270' }, start: 241, end: 270 },
    { id: '271-300', label: { en: '271-300', zh: '271-300' }, start: 271, end: 300 },
    { id: '301+', label: { en: '301+', zh: '301+' }, start: 301, end: 999 }
  ], [])

  const handleRangeClick = useCallback((range) => {
    setQuickNavRange(range)
  }, [setQuickNavRange])

  const handleClearRange = useCallback(() => {
    setQuickNavRange({ id: 'all' })
  }, [setQuickNavRange])

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev)
  }, [])

  const isActive = useCallback((rangeId) => {
    return !searchQuery && activeRange === rangeId
  }, [activeRange, searchQuery])

  const showFilterStatus = activeRange !== 'all' && !searchQuery
  const currentRange = ranges.find(r => r.id === activeRange)
  const currentLabel = currentRange?.label[currentLang] || activeRange

  return (
    <motion.div 
      id="quick-navigation"
      className="space-y-4 sm:space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header - 可折疊控制 */}
      <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiFilter} className="w-4 h-4 text-blue-600 dark:text-blue-300" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {currentLang === 'zh' ? '快速導航' : 'Quick Navigation'}
              </h3>
              {/* 折疊/展開按鈕 - 使用 V 型圖標 */}
              <motion.button
                onClick={toggleCollapse}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                title={isCollapsed 
                  ? (currentLang === 'zh' ? '展開導航' : 'Expand navigation')
                  : (currentLang === 'zh' ? '折疊導航' : 'Collapse navigation')
                }
              >
                <motion.div
                  animate={{ rotate: isCollapsed ? 0 : 180 }}
                  transition={{ duration: 0.2 }}
                >
                  <SafeIcon 
                    icon={FiChevronDown} 
                    className="w-4 h-4 text-gray-600 dark:text-gray-300" 
                  />
                </motion.div>
              </motion.button>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
              {isCollapsed 
                ? (currentLang === 'zh' ? '已折疊 - 點擊展開' : 'Collapsed - Click to expand')
                : (currentLang === 'zh' ? '依銀行代碼範圍篩選' : 'Filter by bank code ranges')
              }
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {searchQuery && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2 text-xs sm:text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-700"
            >
              <SafeIcon icon={FiInfo} className="w-4 h-4" />
              <span className="hidden sm:inline">
                {currentLang === 'zh' ? '搜尋中，篩選已停用' : 'Searching, filters disabled'}
              </span>
              <span className="sm:hidden">
                {currentLang === 'zh' ? '搜尋中' : 'Searching'}
              </span>
            </motion.div>
          )}

          <AnimatePresence>
            {showFilterStatus && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-800/30 dark:to-teal-800/30 border border-emerald-200 dark:border-emerald-600 rounded-lg"
              >
                <div className="flex items-center gap-1 sm:gap-2">
                  <SafeIcon icon={FiTarget} className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600 dark:text-emerald-300" />
                  <span className="font-medium text-emerald-800 dark:text-emerald-200 text-xs sm:text-sm">
                    {currentLabel}
                  </span>
                  <span className="px-1.5 sm:px-2 py-0.5 bg-emerald-200 dark:bg-emerald-700 text-emerald-800 dark:text-emerald-200 text-xs font-semibold rounded-full">
                    {totalCount}
                  </span>
                </div>
                <button
                  onClick={handleClearRange}
                  className="p-1 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-200 hover:bg-emerald-200 dark:hover:bg-emerald-700 rounded-full transition-colors"
                  title={currentLang === 'zh' ? '清除篩選' : 'Clear filter'}
                >
                  <SafeIcon icon={FiX} className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Range Buttons - 可折疊內容 */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6 gap-2 sm:gap-3">
              {ranges.map((range, index) => {
                const active = isActive(range.id)
                const disabled = !!searchQuery
                
                return (
                  <motion.button
                    key={range.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    onClick={() => !disabled && handleRangeClick(range)}
                    disabled={disabled}
                    whileHover={!disabled ? { scale: active ? 1.05 : 1.02 } : {}}
                    whileTap={!disabled ? { scale: active ? 1.02 : 0.98 } : {}}
                    className={`px-2 sm:px-4 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                      disabled
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed opacity-60'
                        : active
                          ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg scale-105'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 shadow-sm hover:shadow-md'
                    }`}
                  >
                    {range.label[currentLang]}
                  </motion.button>
                )
              })}
            </div>

            {/* Statistics */}
            <div className="flex items-center justify-center mt-4 sm:mt-6">
              {!searchQuery && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center text-xs sm:text-sm text-gray-500 dark:text-gray-400 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700"
                >
                  {activeRange === 'all' 
                    ? (currentLang === 'zh' 
                        ? `共 ${originalTotalCount} 家銀行` 
                        : `${originalTotalCount} banks total`
                      )
                    : (currentLang === 'zh'
                        ? `範圍 ${currentLabel}：${totalCount} 家`
                        : `${currentLabel}: ${totalCount} banks`
                      )
                  }
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 折疊狀態下的快速資訊 - 可點擊展開 */}
      <AnimatePresence>
        {isCollapsed && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleCollapse}
            className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 cursor-pointer group"
          >
            <div className="flex items-center gap-3">
              <motion.div
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <SafeIcon icon={FiChevronDown} className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
              </motion.div>
              <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors">
                {currentLang === 'zh' ? '快速導航已折疊' : 'Quick navigation collapsed'}
              </span>
              <span className="text-xs text-gray-400 group-hover:text-blue-500 transition-colors">
                {currentLang === 'zh' ? '(點擊展開)' : '(Click to expand)'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {!searchQuery && activeRange !== 'all' && (
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                  {currentLabel}: {totalCount}
                </span>
              )}
              <motion.div
                animate={{ x: [0, 3, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
                className="text-blue-500 opacity-60"
              >
                <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
              </motion.div>
            </div>
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default QuickNavigation